import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, MessageCircle, AlertTriangle } from 'lucide-react';

const LOGO_URL = "/images/logo-tuprima.png";
const WHATSAPP_SUPPORT = "573206313258";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      
      // Solo admins pueden acceder al sistema
      if (user.is_admin) {
        toast.success(`¡Bienvenido, ${user.first_name}!`);
        navigate('/admin');
      } else {
        // Usuarios normales son redirigidos al home con mensaje de cierre
        toast.info('La plataforma está temporalmente cerrada. Contacta por WhatsApp para devoluciones.');
        navigate('/');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola TuPrima 👋\n\nSolicito información sobre la devolución de mi compra.\n\nAdjunto mi comprobante de pago.`
    );
    window.open(`https://wa.me/${WHATSAPP_SUPPORT}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <img 
            src={LOGO_URL} 
            alt="Tu Prima" 
            className="h-16 mx-auto mb-4"
          />
        </div>

        {/* Mensaje de Cierre */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 text-sm">Plataforma Temporalmente Cerrada</p>
                <p className="text-amber-700 text-xs mt-1">
                  Si compraste tickets y deseas tu devolución, contacta por WhatsApp.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Botón WhatsApp Principal */}
        <Button 
          onClick={openWhatsApp}
          size="lg"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-14 text-lg"
        >
          <MessageCircle className="w-6 h-6 mr-3" />
          Solicitar Devolución: 320 631 3258
        </Button>

        {/* Separador */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-300"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-slate-100 text-slate-500">Acceso solo administradores</span>
          </div>
        </div>

        {/* Login Form (solo para admins) */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-center text-slate-700">Panel Administrativo</CardTitle>
            <CardDescription className="text-center text-xs">
              Solo personal autorizado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs text-slate-600">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email"
                    placeholder="admin@tuprima.com"
                    className="pl-10 h-10 text-sm"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs text-slate-600">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password"
                    placeholder="••••••••"
                    className="pl-10 h-10 text-sm"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-10 rounded-lg bg-slate-700 hover:bg-slate-800"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Acceder'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Link al Home */}
        <div className="text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
